import type { ISocketManager } from "./ISocketManager";

interface SocketRequestOptions<T> {
  /** Defaults to `${event}Answer` */
  answerEvent?: string;
  /** Event the gateway reports failures with, defaults to "error" */
  errorEvent?: string;
  /** Turns the answer into the result; may throw to reject */
  transform?: (response: any) => T;
}

/**
 * Emits `event` and resolves with the next answer. Both listeners are removed
 * once one of them fired - otherwise every request left an error listener
 * behind, and one server error rejected all requests made before.
 */
export function socketRequest<T>(
  socket: ISocketManager | null | undefined,
  event: string,
  payload?: unknown,
  options: SocketRequestOptions<T> = {},
): Promise<T> {
  const answerEvent = options.answerEvent ?? `${event}Answer`;
  const errorEvent = options.errorEvent ?? "error";

  return new Promise<T>((resolve, reject) => {
    const onAnswer = (response: any) => {
      socket?.off(errorEvent, onError);
      try {
        resolve(options.transform ? options.transform(response) : response);
      } catch (error) {
        reject(error);
      }
    };
    const onError = (error: unknown) => {
      socket?.off(answerEvent, onAnswer);
      reject(error);
    };

    if (payload === undefined) socket?.emit(event);
    else socket?.emit(event, payload);
    socket?.once(answerEvent, onAnswer);
    socket?.once(errorEvent, onError);
  });
}
